import { DesignerSource } from '__generated__/types';
import { BriefType, routes } from 'projects/routes';

export interface StepConfiguration {
    step: number;
    title: string;
    requiresAuth?: boolean;
    isDesignerStep?: boolean;
    nextActionLabel?: string;
}

interface Options {
    steps: StepConfiguration[];
    currentStep: number;
    brief: BriefType;
    designerSources: DesignerSource[];
}

export class StepState {
    steps: StepConfiguration[];
    number: number;
    brief: BriefType;
    designerSources: DesignerSource[];

    constructor({ steps, currentStep, brief, designerSources }: Options) {
        this.steps = steps;
        this.brief = brief;
        this.number = currentStep;
        this.designerSources = designerSources;
    }

    currentStep() {
        const currentStep = this.steps.find(step => step.step === this.number);
        if (currentStep) {
            return currentStep;
        } else {
            throw new Error('Current step does not exist');
        }
    }

    hasStepAfter() {
        return this.steps.map(step => step.step).includes(this.number + 1);
    }

    hasStepBefore() {
        return this.steps.map(step => step.step).includes(this.number - 1);
    }

    nextStep() {
        const nextStep = this.steps.find(step => step.step === this.number + 1);
        return nextStep || this.currentStep();
    }

    prevStep() {
        const prevStep = this.steps.find(step => step.step === this.number - 1);
        return prevStep || this.currentStep();
    }

    nextUrl() {
        return routes.brief(this.brief, this.nextStep().step);
    }

    prevUrl() {
        return routes.brief(this.brief, this.prevStep().step);
    }

    percentageComplete() {
        return (this.number / this.steps.length) * 100;
    }

    stepCount() {
        return this.steps.length;
    }

    nextRequiresAuth() {
        return this.nextStep().requiresAuth;
    }

    isDesignerStep() {
        return this.currentStep().isDesignerStep;
    }

    nextActionLabel() {
        const nextActionLabel = this.currentStep().nextActionLabel;
        if (nextActionLabel) {
            return nextActionLabel;
        }

        if (!this.hasStepAfter()) {
            return __("Let's do this!");
        } else {
            return __('Next');
        }
    }
}

export const createStepState = ({
    steps,
    currentStep,
    brief,
    designerSources,
}: Options): StepState => {
    // Remove the find designers step if all our designers are direct
    if (designerSources.every(src => src == DesignerSource.DIRECT) && designerSources.length > 0) {
        steps = steps.filter(step => !step.isDesignerStep);
    }

    return new StepState({ steps, currentStep, brief, designerSources });
};
